import { subject } from '@casl/ability';
import { CommercialFeatureFlags, FeatureFlags } from '@lightdash/common';
import { Box, ScrollArea, Stack, Text, Title } from '@mantine-8/core';
import {
    IconAppWindow,
    IconApps,
    IconBolt,
    IconBrain,
    IconBrowser,
    IconBuildingSkyscraper,
    IconCalendarStats,
    IconChecklist,
    IconClock,
    IconDatabase,
    IconDatabaseCog,
    IconDatabaseExport,
    IconFolders,
    IconHistory,
    IconIdBadge2,
    IconKey,
    IconLock,
    IconBrush,
    IconPalette,
    IconPlug,
    IconRefresh,
    IconReportAnalytics,
    IconShieldCheck,
    IconTableOptions,
    IconTrash,
    IconUserCircle,
    IconUserCode,
    IconUserPlus,
    IconUsers,
    IconUserShield,
    IconVariable,
} from '@tabler/icons-react';
import { useMemo, type FC } from 'react';
import {
    matchPath,
    Navigate,
    useLocation,
    useRoutes,
    type RouteObject,
} from 'react-router';
import ErrorState from '../components/common/ErrorState';
import MantineIcon from '../components/common/MantineIcon';
import Page from '../components/common/Page/Page';
import PageBreadcrumbs from '../components/common/PageBreadcrumbs';
import RouterNavLink from '../components/common/RouterNavLink';
import { SettingsGridCard } from '../components/common/Settings/SettingsCard';
import PageSpinner from '../components/PageSpinner';
import AccessTokensPanel from '../components/UserSettings/AccessTokensPanel';
import AllowedDomainsPanel from '../components/UserSettings/AllowedDomainsPanel';
import AppearanceSettingsPanel from '../components/UserSettings/AppearanceSettingsPanel';
import DefaultProjectPanel from '../components/UserSettings/DefaultProjectPanel';
import { DeleteOrganizationPanel } from '../components/UserSettings/DeleteOrganizationPanel';
import GithubSettingsPanel from '../components/UserSettings/GithubSettingsPanel';
import GitlabSettingsPanel from '../components/UserSettings/GitlabSettingsPanel';
import ImpersonationPanel from '../components/UserSettings/ImpersonationPanel';
import { LeaveOrganizationPanel } from '../components/UserSettings/LeaveOrganizationPanel';
import MyAppsPanel from '../components/UserSettings/MyAppsPanel';
import { MyWarehouseConnectionsPanel } from '../components/UserSettings/MyWarehouseConnectionsPanel';
import OAuthClientsPanel from '../components/UserSettings/OAuthClientsPanel';
import OrganizationPanel from '../components/UserSettings/OrganizationPanel';
import AccountLinkingPanel from '../components/UserSettings/OrganizationSso/AccountLinkingPanel';
import AzureAdSsoPanel from '../components/UserSettings/OrganizationSso/AzureAdSsoPanel';
import GenericOidcSsoPanel from '../components/UserSettings/OrganizationSso/GenericOidcSsoPanel';
import GoogleSsoPanel from '../components/UserSettings/OrganizationSso/GoogleSsoPanel';
import OktaSsoPanel from '../components/UserSettings/OrganizationSso/OktaSsoPanel';
import OneLoginSsoPanel from '../components/UserSettings/OrganizationSso/OneLoginSsoPanel';
import { OrganizationWarehouseCredentialsPanel } from '../components/UserSettings/OrganizationWarehouseCredentialsPanel';
import PasswordPanel from '../components/UserSettings/PasswordPanel';
import ProfilePanel from '../components/UserSettings/ProfilePanel';
import ProjectManagementPanel from '../components/UserSettings/ProjectManagementPanel';
import UserScheduledDeliveriesPanel from '../components/UserSettings/ScheduledDeliveriesPanel';
import SlackSettingsPanel from '../components/UserSettings/SlackSettingsPanel';
import SocialLoginsPanel from '../components/UserSettings/SocialLoginsPanel';
import UserAttributesPanel from '../components/UserSettings/UserAttributesPanel';
import UsersAndGroupsPanel from '../components/UserSettings/UsersAndGroupsPanel';
import { useAiOrganizationSettings } from '../ee/features/aiCopilot/hooks/useAiOrganizationSettings';
import ScimAccessTokensPanel from '../ee/features/scim/components/ScimAccessTokensPanel';
import { ServiceAccountsPage } from '../ee/features/serviceAccounts';
import { CustomRoleCreate } from '../ee/pages/customRoles/CustomRoleCreate';
import { CustomRoleEdit } from '../ee/pages/customRoles/CustomRoleEdit';
import { CustomRoles } from '../ee/pages/customRoles/CustomRoles';
import DesignListPage from '../features/organizationDesigns/components/DesignListPage';
import { useOrganization } from '../hooks/organization/useOrganization';
import { useActiveProjectUuid } from '../hooks/useActiveProject';
import { useProject } from '../hooks/useProject';
import { useServerFeatureFlag } from '../hooks/useServerOrClientFeatureFlag';
import { Can } from '../providers/Ability';
import useApp from '../providers/App/useApp';
import { TrackPage } from '../providers/Tracking/TrackingProvider';
import useTracking from '../providers/Tracking/useTracking';
import { EventName, PageName } from '../types/Events';
import ProjectSettings from './ProjectSettings';
import classes from './Settings.module.css';

const Settings: FC = () => {
    const { data: embeddingEnabled } = useServerFeatureFlag(
        CommercialFeatureFlags.Embedding,
    );

    const { data: isScimTokenManagementEnabled } = useServerFeatureFlag(
        CommercialFeatureFlags.Scim,
    );

    const aiOrganizationSettingsQuery = useAiOrganizationSettings();
    const isAiCopilotEnabledOrTrial =
        aiOrganizationSettingsQuery.isSuccess &&
        (aiOrganizationSettingsQuery.data.isCopilotEnabled ||
            aiOrganizationSettingsQuery.data.isTrial);

    const { data: serviceAccountsFlag } = useServerFeatureFlag(
        CommercialFeatureFlags.ServiceAccounts,
    );
    const isServiceAccountFeatureFlagEnabled =
        serviceAccountsFlag?.enabled ?? false;

    const {
        health: {
            data: health,
            isInitialLoading: isHealthLoading,
            error: healthError,
        },
        user: { data: user, isInitialLoading: isUserLoading, error: userError },
    } = useApp();

    const { data: isUserImpersonationEnabled } = useServerFeatureFlag(
        FeatureFlags.UserImpersonation,
    );

    const showImpersonationPanel =
        isUserImpersonationEnabled?.enabled &&
        user?.ability?.can('update', 'Organization');

    const { data: leaveOrganizationFlag } = useServerFeatureFlag(
        FeatureFlags.LeaveOrganization,
    );
    const isLeaveOrganizationEnabled = leaveOrganizationFlag?.enabled === true;

    const { data: customRolesFlag } = useServerFeatureFlag(
        CommercialFeatureFlags.CustomRoles,
    );
    const isCustomRolesEnabled =
        health?.isCustomRolesEnabled || customRolesFlag?.enabled;

    const userGroupsFeatureFlagQuery = useServerFeatureFlag(
        FeatureFlags.UserGroupsEnabled,
    );

    const { data: dataAppsFlag } = useServerFeatureFlag(
        FeatureFlags.EnableDataApps,
    );

    const { data: ssoOrganizationSettingsFlag } = useServerFeatureFlag(
        FeatureFlags.SsoOrganizationSettings,
    );
    const isSsoOrganizationSettingsEnabled =
        ssoOrganizationSettingsFlag?.enabled ?? false;

    const { track } = useTracking();
    const {
        data: organization,
        isInitialLoading: isOrganizationLoading,
        error: organizationError,
    } = useOrganization();
    const { activeProjectUuid, isLoading: isActiveProjectUuidLoading } =
        useActiveProjectUuid();
    const {
        data: project,
        isInitialLoading: isProjectLoading,
        error: projectError,
    } = useProject(activeProjectUuid);

    const allowPasswordAuthentication =
        !health?.auth.disablePasswordAuthentication;

    const hasSocialLogin =
        health?.auth.google.enabled ||
        health?.auth.okta.enabled ||
        health?.auth.oneLogin.enabled ||
        health?.auth.azuread.enabled ||
        health?.auth.oidc.enabled;

    const isGroupManagementEnabled =
        userGroupsFeatureFlagQuery.data?.enabled ?? false;

    // This allows us to enable service accounts in the UI for on-premise installations
    const isServiceAccountsEnabled =
        health?.isServiceAccountEnabled || isServiceAccountFeatureFlagEnabled;

    const { data: warehouseCredentialsFlag } = useServerFeatureFlag(
        CommercialFeatureFlags.OrganizationWarehouseCredentials,
    );
    const isWarehouseCredentialsFeatureFlagEnabled =
        warehouseCredentialsFlag?.enabled ?? false;

    // This allows us to enable organization warehouse credentials in the UI for on-premise installations
    const isWarehouseCredentialsEnabled =
        (health?.isOrganizationWarehouseCredentialsEnabled ?? false) ||
        isWarehouseCredentialsFeatureFlagEnabled;

    const routes = useMemo<RouteObject[]>(() => {
        const allowedRoutes: RouteObject[] = [
            {
                path: '/appearance',
                element: <AppearanceSettingsPanel />,
            },
            {
                path: '/profile',
                element: (
                    <Stack gap="xl">
                        <SettingsGridCard>
                            <Title order={4}>Profile settings</Title>
                            <ProfilePanel />
                        </SettingsGridCard>
                        {isLeaveOrganizationEnabled && (
                            <SettingsGridCard>
                                <Box>
                                    <Title order={4}>Danger zone</Title>
                                    <Text c="ldGray.6" fz="xs">
                                        Leave the organization to remove
                                        yourself from it (you cannot leave if
                                        you are the only admin). This action is
                                        not reversible.
                                    </Text>
                                </Box>
                                <LeaveOrganizationPanel />
                            </SettingsGridCard>
                        )}
                    </Stack>
                ),
            },
            {
                path: '*',
                element: <Navigate to="/generalSettings/profile" />,
            },
        ];

        if (allowPasswordAuthentication) {
            allowedRoutes.push({
                path: '/password',
                element: (
                    <Stack gap="xl">
                        <SettingsGridCard>
                            <Title order={4}>Password settings</Title>
                            <PasswordPanel />
                        </SettingsGridCard>

                        {hasSocialLogin && (
                            <SettingsGridCard>
                                <Title order={4}>Social logins</Title>
                                <SocialLoginsPanel />
                            </SettingsGridCard>
                        )}
                    </Stack>
                ),
            });
        }
        allowedRoutes.push({
            path: '/myWarehouseConnections',
            element: (
                <Stack gap="xl">
                    <MyWarehouseConnectionsPanel />
                </Stack>
            ),
        });
        if (user?.ability.can('create', 'ScheduledDeliveries')) {
            // A user might not be able to create scheduled permissions on the org level but on a specific project
            // level. The check here makes sure that the user has the ability to create a scheduled delivery at least somewhere.
            // Since the service returns specifically the user's scheduled deliveries, this is completely intended behavior.
            allowedRoutes.push({
                path: '/userScheduledDeliveries',
                element: (
                    <Stack gap="xl">
                        <SettingsGridCard>
                            <Title order={4}>My scheduled deliveries</Title>
                        </SettingsGridCard>
                        <UserScheduledDeliveriesPanel />
                    </Stack>
                ),
            });
        }
        if (dataAppsFlag?.enabled && user?.ability.can('create', 'DataApp')) {
            allowedRoutes.push({
                path: '/myApps',
                element: (
                    <Stack gap="xl">
                        <SettingsGridCard>
                            <Title order={4}>My apps</Title>
                        </SettingsGridCard>
                        <MyAppsPanel />
                    </Stack>
                ),
            });
        }
        if (user?.ability.can('manage', 'PersonalAccessToken')) {
            allowedRoutes.push({
                path: '/organization',
                element: (
                    <Stack gap="xl">
                        <SettingsGridCard>
                            <Title order={4}>General</Title>
                            <OrganizationPanel />
                        </SettingsGridCard>

                        <SettingsGridCard>
                            <div>
                                <Title order={4}>Allowed email domains</Title>
                                <Text c="ldGray.6" fz="xs">
                                    Anyone with email addresses at these domains
                                    can automatically join the organization.
                                </Text>
                            </div>
                            <AllowedDomainsPanel />
                        </SettingsGridCard>

                        <SettingsGridCard>
                            <div>
                                <Title order={4}>Default Project</Title>
                                <Text c="ldGray.6" fz="xs">
                                    This is the project users will see when they
                                    log in for the first time or from a new
                                    device. If a user does not have access, they
                                    will see their next accessible project.
                                </Text>
                            </div>
                            <DefaultProjectPanel />
                        </SettingsGridCard>

                        {showImpersonationPanel && (
                            <SettingsGridCard>
                                <Title order={4}>User impersonation</Title>
                                <ImpersonationPanel />
                            </SettingsGridCard>
                        )}

                        {(isLeaveOrganizationEnabled ||
                            user.ability?.can('delete', 'Organization')) && (
                            <SettingsGridCard>
                                <div>
                                    <Title order={4}>Danger zone </Title>
                                    <Text c="ldGray.6" fz="xs">
                                        {isLeaveOrganizationEnabled &&
                                            'Leave the organization to remove yourself from it (you cannot leave if you are the only admin). '}
                                        {user.ability?.can(
                                            'delete',
                                            'Organization',
                                        ) &&
                                            'Deleting the organization removes the whole workspace and all its content, including users. '}
                                        These actions are not reversible.
                                    </Text>
                                </div>
                                <Stack gap="sm" align="flex-end">
                                    {isLeaveOrganizationEnabled && (
                                        <LeaveOrganizationPanel />
                                    )}
                                    {user.ability?.can(
                                        'delete',
                                        'Organization',
                                    ) && <DeleteOrganizationPanel />}
                                </Stack>
                            </SettingsGridCard>
                        )}
                    </Stack>
                ),
            });
        }
        if (
            user?.ability.can(
                'manage',
                subject('OrganizationMemberProfile', {
                    organizationUuid: organization?.organizationUuid,
                }),
            )
        ) {
            allowedRoutes.push({
                path: '/userManagement',
                element: <UsersAndGroupsPanel />,
            });
        }

        if (
            user?.ability.can(
                'manage',
                subject('Organization', {
                    organizationUuid: organization?.organizationUuid,
                }),
            )
        ) {
            allowedRoutes.push({
                path: '/userAttributes',
                element: <UserAttributesPanel />,
            });
        }
        if (
            user?.ability.can(
                'manage',
                subject('OrganizationWarehouseCredentials', {
                    organizationUuid: organization?.organizationUuid,
                }),
            )
        ) {
            allowedRoutes.push({
                path: '/warehouseCredentials',
                element: <OrganizationWarehouseCredentialsPanel />,
            });
        }
        if (
            organization &&
            !organization.needsProject &&
            user?.ability.can('view', 'Project')
        ) {
            allowedRoutes.push({
                path: '/projectManagement',
                element: <ProjectManagementPanel />,
            });
        }

        if (
            project &&
            organization &&
            !organization.needsProject &&
            user?.ability.can(
                'update',
                subject('Project', {
                    organizationUuid: organization.organizationUuid,
                    projectUuid: project.projectUuid,
                }),
            )
        ) {
            allowedRoutes.push({
                path: '/projectManagement/:projectUuid/*',
                element: (
                    <TrackPage name={PageName.PROJECT_SETTINGS}>
                        <ProjectSettings />
                    </TrackPage>
                ),
            });
        }
        if (user?.ability.can('manage', 'PersonalAccessToken')) {
            allowedRoutes.push({
                path: '/personalAccessTokens',
                element: <AccessTokensPanel />,
            });
        }

        if (
            dataAppsFlag?.enabled &&
            user?.ability.can('view', 'OrganizationDesign')
        ) {
            allowedRoutes.push({
                path: '/themes',
                element: <DesignListPage />,
            });
        }

        if (user?.ability.can('manage', 'Organization')) {
            allowedRoutes.push({
                path: '/integrations',
                element: (
                    <Stack>
                        <Title order={4}>Integrations</Title>
                        {!health?.hasSlack &&
                            !health?.hasGithub &&
                            !health?.hasGitlab &&
                            'No integrations available'}
                        {health?.hasSlack && <SlackSettingsPanel />}
                        {health?.hasGithub && <GithubSettingsPanel />}
                        {health?.hasGitlab && <GitlabSettingsPanel />}
                    </Stack>
                ),
            });
        }

        if (user?.ability.can('manage', 'Organization')) {
            allowedRoutes.push({
                path: '/oauthClients',
                element: <OAuthClientsPanel />,
            });
        }

        if (
            user?.ability.can('manage', 'Organization') &&
            isSsoOrganizationSettingsEnabled
        ) {
            allowedRoutes.push({
                path: '/sso',
                element: (
                    <Stack gap="md">
                        <Stack gap="xs">
                            <Title order={5}>Single Sign-On</Title>
                            <Text c="ldGray.6" fz="xs">
                                Configure SSO providers for your organization.
                                Users are routed to the matching provider based
                                on their email domain.
                            </Text>
                        </Stack>
                        <AzureAdSsoPanel />
                        <OktaSsoPanel />
                        <GenericOidcSsoPanel />
                        <OneLoginSsoPanel />
                        {/* Google has no per-org credentials; only show the
                            toggle when Google is enabled instance-wide. */}
                        {health?.auth.google.enabled && <GoogleSsoPanel />}
                        <AccountLinkingPanel />
                    </Stack>
                ),
            });
        }

        // Commercial route
        if (
            user?.ability.can('manage', 'Organization') &&
            isScimTokenManagementEnabled?.enabled
        ) {
            allowedRoutes.push({
                path: '/scimAccessTokens',
                element: <ScimAccessTokensPanel />,
            });
        }

        if (
            user?.ability.can('manage', 'Organization') &&
            isServiceAccountsEnabled
        ) {
            allowedRoutes.push({
                path: '/serviceAccounts',
                element: <ServiceAccountsPage />,
            });
        }

        if (
            user?.ability.can('manage', 'Organization') &&
            isCustomRolesEnabled
        ) {
            allowedRoutes.push({
                path: '/customRoles',
                element: <CustomRoles />,
            });
            allowedRoutes.push({
                path: '/customRoles/create',
                element: <CustomRoleCreate />,
            });
            allowedRoutes.push({
                path: '/customRoles/:roleId',
                element: <CustomRoleEdit />,
            });
        }

        return allowedRoutes;
    }, [
        allowPasswordAuthentication,
        user?.ability,
        organization,
        project,
        isScimTokenManagementEnabled?.enabled,
        isServiceAccountsEnabled,
        isCustomRolesEnabled,
        hasSocialLogin,
        showImpersonationPanel,
        health?.hasSlack,
        health?.hasGithub,
        health?.hasGitlab,
        health?.auth.google.enabled,
        dataAppsFlag?.enabled,
        isSsoOrganizationSettingsEnabled,
        isLeaveOrganizationEnabled,
    ]);
    const routeElements = useRoutes(routes);

    const location = useLocation();
    const isFixedContent = useMemo(() => {
        return (
            !matchPath(
                {
                    path: '/generalSettings/projectManagement',
                    end: true,
                },
                location.pathname,
            ) &&
            !matchPath(
                {
                    path: '/generalSettings/projectManagement/:projectUuid/changesets',
                },
                location.pathname,
            ) &&
            !matchPath(
                {
                    path: '/generalSettings/projectManagement/:projectUuid/scheduledDeliveries',
                },
                location.pathname,
            ) &&
            !matchPath(
                {
                    path: '/generalSettings/userScheduledDeliveries',
                },
                location.pathname,
            ) &&
            !matchPath(
                {
                    path: '/generalSettings/myApps',
                },
                location.pathname,
            ) &&
            !matchPath(
                {
                    path: '/generalSettings/projectManagement/:projectUuid/compilationHistory',
                },
                location.pathname,
            ) &&
            !matchPath(
                {
                    path: '/generalSettings/projectManagement/:projectUuid/recentlyDeleted',
                },
                location.pathname,
            ) &&
            !matchPath(
                {
                    path: '/generalSettings/customRoles',
                },
                location.pathname,
            ) &&
            !matchPath(
                {
                    path: '/generalSettings/customRoles/:roleId',
                },
                location.pathname,
            ) &&
            !matchPath(
                {
                    path: '/generalSettings/serviceAccounts',
                },
                location.pathname,
            ) &&
            !matchPath(
                {
                    path: '/generalSettings/projectManagement/:projectUuid/preAggregates/*',
                },
                location.pathname,
            ) &&
            !matchPath(
                {
                    path: '/generalSettings/projectManagement/:projectUuid/validator',
                },
                location.pathname,
            )
        );
    }, [location.pathname]);

    if (
        isHealthLoading ||
        isUserLoading ||
        isOrganizationLoading ||
        isActiveProjectUuidLoading ||
        isProjectLoading
    ) {
        return <PageSpinner />;
    }

    if (userError || healthError || organizationError || projectError) {
        return (
            <ErrorState
                error={
                    userError?.error ||
                    healthError?.error ||
                    organizationError?.error ||
                    projectError?.error
                }
            />
        );
    }

    if (!health || !user || !organization) return null;

    return (
        <Page
            withFullHeight
            withSidebarFooter
            withFixedContent={isFixedContent}
            withPaddedContent
            title="Settings"
            sidebar={
                <Stack className={classes.sidebarStack}>
                    <PageBreadcrumbs
                        items={[{ title: 'Settings', active: true }]}
                    />
                    <ScrollArea
                        variant="primary"
                        offsetScrollbars
                        scrollbarSize={8}
                    >
                        <Stack gap="lg">
                            <Box>
                                <Title order={6} fw={600} mb="xs">
                                    Your settings
                                </Title>
                                <RouterNavLink
                                    exact
                                    to="/generalSettings/profile"
                                    label="Profile"
                                    leftSection={
                                        <MantineIcon icon={IconUserCircle} />
                                    }
                                />
                                {allowPasswordAuthentication && (
                                    <RouterNavLink
                                        label={
                                            hasSocialLogin
                                                ? 'Password & Social Logins'
                                                : 'Password'
                                        }
                                        exact
                                        to="/generalSettings/password"
                                        leftSection={
                                            <MantineIcon icon={IconLock} />
                                        }
                                    />
                                )}
                                <RouterNavLink
                                    label="My warehouse connections"
                                    exact
                                    to="/generalSettings/myWarehouseConnections"
                                    leftSection={
                                        <MantineIcon icon={IconDatabaseCog} />
                                    }
                                />
                                {/*A user might not be able to create scheduled
                                permissions on the org level but on a specific
                                project level. The check here makes sure that
                                the user has the ability to create a scheduled
                                delivery at least somewhere. Since the
                                service returns specifically the user's
                                scheduled deliveries, this is completely
                                intended behavior.*/}
                                {user.ability.can(
                                    'create',
                                    'ScheduledDeliveries',
                                ) && (
                                    <RouterNavLink
                                        label="My scheduled deliveries"
                                        exact
                                        to="/generalSettings/userScheduledDeliveries"
                                        leftSection={
                                            <MantineIcon
                                                icon={IconCalendarStats}
                                            />
                                        }
                                    />
                                )}
                                {dataAppsFlag?.enabled &&
                                    user.ability.can('create', 'DataApp') && (
                                        <RouterNavLink
                                            label="My apps"
                                            exact
                                            to="/generalSettings/myApps"
                                            leftSection={
                                                <MantineIcon
                                                    icon={IconAppWindow}
                                                />
                                            }
                                        />
                                    )}
                                {user.ability.can(
                                    'manage',
                                    'PersonalAccessToken',
                                ) && (
                                    <RouterNavLink
                                        label="Personal access tokens"
                                        exact
                                        to="/generalSettings/personalAccessTokens"
                                        leftSection={
                                            <MantineIcon icon={IconKey} />
                                        }
                                    />
                                )}
                            </Box>

                            <Box>
                                <Title order={6} fw={600} mb="xs">
                                    Organization settings
                                </Title>

                                {user.ability.can('manage', 'Organization') && (
                                    <RouterNavLink
                                        label="General"
                                        to="/generalSettings/organization"
                                        exact
                                        leftSection={
                                            <MantineIcon
                                                icon={IconBuildingSkyscraper}
                                            />
                                        }
                                    />
                                )}
                                {isCustomRolesEnabled && (
                                    <Can I="manage" a="Organization">
                                        <RouterNavLink
                                            label="Custom roles"
                                            to="/generalSettings/customRoles"
                                            exact
                                            leftSection={
                                                <MantineIcon
                                                    icon={IconIdBadge2}
                                                />
                                            }
                                        />
                                    </Can>
                                )}

                                {user.ability.can(
                                    'update',
                                    'OrganizationMemberProfile',
                                ) && (
                                    <RouterNavLink
                                        label={
                                            isGroupManagementEnabled
                                                ? 'Users & groups'
                                                : 'User management'
                                        }
                                        to="/generalSettings/userManagement"
                                        exact
                                        leftSection={
                                            <MantineIcon icon={IconUserPlus} />
                                        }
                                    />
                                )}
                                {user.ability.can(
                                    'manage',
                                    subject('Organization', {
                                        organizationUuid:
                                            organization.organizationUuid,
                                    }),
                                ) && (
                                    <RouterNavLink
                                        label={
                                            isGroupManagementEnabled
                                                ? 'User & group attributes'
                                                : 'User attributes'
                                        }
                                        to="/generalSettings/userAttributes"
                                        exact
                                        leftSection={
                                            <MantineIcon
                                                icon={IconUserShield}
                                            />
                                        }
                                    />
                                )}

                                {user.ability.can('update', 'Organization') && (
                                    <RouterNavLink
                                        label="Appearance"
                                        exact
                                        to="/generalSettings/appearance"
                                        leftSection={
                                            <MantineIcon icon={IconPalette} />
                                        }
                                    />
                                )}

                                {dataAppsFlag?.enabled &&
                                    user.ability.can(
                                        'view',
                                        'OrganizationDesign',
                                    ) && (
                                        <RouterNavLink
                                            label="Themes"
                                            exact
                                            to="/generalSettings/themes"
                                            leftSection={
                                                <MantineIcon icon={IconBrush} />
                                            }
                                        />
                                    )}

                                {user.ability.can('manage', 'Organization') && (
                                    <RouterNavLink
                                        label="Integrations"
                                        exact
                                        to="/generalSettings/integrations"
                                        leftSection={
                                            <MantineIcon icon={IconPlug} />
                                        }
                                    />
                                )}

                                {user.ability.can('manage', 'Organization') && (
                                    <RouterNavLink
                                        label="OAuth applications"
                                        exact
                                        to="/generalSettings/oauthClients"
                                        leftSection={
                                            <MantineIcon icon={IconApps} />
                                        }
                                    />
                                )}

                                {user.ability.can('manage', 'Organization') &&
                                    isSsoOrganizationSettingsEnabled && (
                                        <RouterNavLink
                                            label="Single Sign-On"
                                            exact
                                            to="/generalSettings/sso"
                                            leftSection={
                                                <MantineIcon icon={IconLock} />
                                            }
                                        />
                                    )}

                                {user.ability.can(
                                    'manage',
                                    subject(
                                        'OrganizationWarehouseCredentials',
                                        {
                                            organizationUuid:
                                                organization?.organizationUuid,
                                        },
                                    ),
                                ) &&
                                    isWarehouseCredentialsEnabled && (
                                        <RouterNavLink
                                            label="Warehouse credentials"
                                            exact
                                            to="/generalSettings/warehouseCredentials"
                                            leftSection={
                                                <MantineIcon
                                                    icon={IconDatabaseCog}
                                                />
                                            }
                                        />
                                    )}

                                {organization &&
                                    !organization.needsProject &&
                                    user.ability.can('view', 'Project') && (
                                        <RouterNavLink
                                            label="All projects"
                                            to="/generalSettings/projectManagement"
                                            exact
                                            leftSection={
                                                <MantineIcon
                                                    icon={IconDatabase}
                                                />
                                            }
                                        />
                                    )}

                                {user.ability.can('manage', 'Organization') &&
                                    isScimTokenManagementEnabled?.enabled && (
                                        <RouterNavLink
                                            label="SCIM Access Tokens"
                                            exact
                                            to="/generalSettings/scimAccessTokens"
                                            leftSection={
                                                <MantineIcon icon={IconKey} />
                                            }
                                        />
                                    )}
                                {user.ability.can('manage', 'Organization') &&
                                    isServiceAccountsEnabled && (
                                        <RouterNavLink
                                            label="Service Accounts"
                                            exact
                                            to="/generalSettings/serviceAccounts"
                                            leftSection={
                                                <MantineIcon
                                                    icon={IconUserCode}
                                                />
                                            }
                                        />
                                    )}
                                {isAiCopilotEnabledOrTrial &&
                                    user.ability.can(
                                        'manage',
                                        subject('AiAgent', {
                                            organizationUuid:
                                                organization.organizationUuid,
                                        }),
                                    ) && (
                                        <RouterNavLink
                                            label="AI Agents"
                                            exact
                                            to="/ai-agents/admin"
                                            leftSection={
                                                <MantineIcon icon={IconBrain} />
                                            }
                                        />
                                    )}
                            </Box>

                            {organization &&
                            !organization.needsProject &&
                            project &&
                            user.ability.can(
                                'update',
                                subject('Project', {
                                    organizationUuid:
                                        organization.organizationUuid,
                                    projectUuid: project.projectUuid,
                                }),
                            ) ? (
                                <Box>
                                    <Title order={6} fw={600} mb="xs">
                                        Current project ({project?.name})
                                    </Title>

                                    <RouterNavLink
                                        label="Connection settings"
                                        exact
                                        to={`/generalSettings/projectManagement/${project.projectUuid}/settings`}
                                        leftSection={
                                            <MantineIcon
                                                icon={IconDatabaseCog}
                                            />
                                        }
                                    />

                                    <RouterNavLink
                                        label="Tables configuration"
                                        exact
                                        to={`/generalSettings/projectManagement/${project.projectUuid}/tablesConfiguration`}
                                        leftSection={
                                            <MantineIcon
                                                icon={IconTableOptions}
                                            />
                                        }
                                    />

                                    <RouterNavLink
                                        label="Changesets"
                                        exact
                                        to={`/generalSettings/projectManagement/${project.projectUuid}/changesets`}
                                        leftSection={
                                            <MantineIcon icon={IconHistory} />
                                        }
                                    />

                                    <RouterNavLink
                                        label="Compilation history"
                                        exact
                                        to={`/generalSettings/projectManagement/${project.projectUuid}/compilationHistory`}
                                        leftSection={
                                            <MantineIcon icon={IconRefresh} />
                                        }
                                    />

                                    {/* TODO: Consider adding a setting to disable pre-aggregate materializations on preview projects,
                                        or turn them off by default. Currently materializations still run on previews,
                                        which can be resource-intensive. Admins/developers should be able to control this. */}
                                    {health.preAggregates.enabled && (
                                        <RouterNavLink
                                            label="Pre-aggregates"
                                            exact
                                            to={`/generalSettings/projectManagement/${project.projectUuid}/preAggregates`}
                                            leftSection={
                                                <MantineIcon icon={IconBolt} />
                                            }
                                            defaultOpened={location.pathname.includes(
                                                `/projectManagement/${project.projectUuid}/preAggregates`,
                                            )}
                                        >
                                            <RouterNavLink
                                                label="Materializations"
                                                exact
                                                to={`/generalSettings/projectManagement/${project.projectUuid}/preAggregates/materializations`}
                                                leftSection={
                                                    <MantineIcon
                                                        icon={IconDatabase}
                                                    />
                                                }
                                            />
                                            <RouterNavLink
                                                label="Analytics"
                                                exact
                                                to={`/generalSettings/projectManagement/${project.projectUuid}/preAggregates/audit`}
                                                leftSection={
                                                    <MantineIcon
                                                        icon={
                                                            IconReportAnalytics
                                                        }
                                                    />
                                                }
                                            />
                                        </RouterNavLink>
                                    )}

                                    <RouterNavLink
                                        label="Parameters"
                                        exact
                                        to={`/generalSettings/projectManagement/${project.projectUuid}/parameters`}
                                        leftSection={
                                            <MantineIcon icon={IconVariable} />
                                        }
                                    />

                                    <Can
                                        I="update"
                                        this={subject('Project', {
                                            organizationUuid:
                                                organization.organizationUuid,
                                            projectUuid: project.projectUuid,
                                        })}
                                    >
                                        <RouterNavLink
                                            label="Project time zone"
                                            exact
                                            to={`/generalSettings/projectManagement/${project.projectUuid}/queryTimezone`}
                                            leftSection={
                                                <MantineIcon icon={IconClock} />
                                            }
                                        />
                                    </Can>

                                    <Can
                                        I="update"
                                        this={subject('Project', {
                                            organizationUuid:
                                                organization.organizationUuid,
                                            projectUuid: project.projectUuid,
                                        })}
                                    >
                                        <RouterNavLink
                                            label="Preview settings"
                                            exact
                                            to={`/generalSettings/projectManagement/${project.projectUuid}/previewsConfig`}
                                            leftSection={
                                                <MantineIcon
                                                    icon={IconUserCode}
                                                />
                                            }
                                        />
                                    </Can>

                                    <Can
                                        I="update"
                                        this={subject('Project', {
                                            organizationUuid:
                                                organization.organizationUuid,
                                            projectUuid: project.projectUuid,
                                        })}
                                    >
                                        <RouterNavLink
                                            label="Appearance"
                                            exact
                                            to={`/generalSettings/projectManagement/${project.projectUuid}/appearance`}
                                            leftSection={
                                                <MantineIcon
                                                    icon={IconPalette}
                                                />
                                            }
                                        />
                                    </Can>

                                    <Can
                                        I="manage"
                                        this={subject('Project', {
                                            organizationUuid:
                                                organization.organizationUuid,
                                            projectUuid: project.projectUuid,
                                        })}
                                    >
                                        <RouterNavLink
                                            label="Project access"
                                            exact
                                            to={`/generalSettings/projectManagement/${project.projectUuid}/projectAccess`}
                                            leftSection={
                                                <MantineIcon icon={IconUsers} />
                                            }
                                        />
                                        <RouterNavLink
                                            label="Default user spaces"
                                            exact
                                            to={`/generalSettings/projectManagement/${project.projectUuid}/defaultUserSpaces`}
                                            leftSection={
                                                <MantineIcon
                                                    icon={IconFolders}
                                                />
                                            }
                                        />
                                    </Can>

                                    {user.ability.can(
                                        'view',
                                        subject('Analytics', {
                                            organizationUuid:
                                                organization.organizationUuid,
                                            projectUuid: project.projectUuid,
                                        }),
                                    ) ? (
                                        <RouterNavLink
                                            label="Usage analytics"
                                            exact
                                            to={`/generalSettings/projectManagement/${project.projectUuid}/usageAnalytics`}
                                            onClick={() => {
                                                track({
                                                    name: EventName.USAGE_ANALYTICS_CLICKED,
                                                });
                                            }}
                                            leftSection={
                                                <MantineIcon
                                                    icon={IconReportAnalytics}
                                                />
                                            }
                                        />
                                    ) : null}

                                    <RouterNavLink
                                        label="Syncs & Scheduled deliveries"
                                        exact
                                        to={`/generalSettings/projectManagement/${project.projectUuid}/scheduledDeliveries`}
                                        leftSection={
                                            <MantineIcon
                                                icon={IconCalendarStats}
                                            />
                                        }
                                    />

                                    {user.ability?.can(
                                        'update',
                                        subject('Project', {
                                            organizationUuid:
                                                project.organizationUuid,
                                            projectUuid: project.projectUuid,
                                        }),
                                    ) && embeddingEnabled?.enabled ? (
                                        <RouterNavLink
                                            label="Embed configuration"
                                            exact
                                            to={`/generalSettings/projectManagement/${project.projectUuid}/embed`}
                                            leftSection={
                                                <MantineIcon
                                                    icon={IconBrowser}
                                                />
                                            }
                                        />
                                    ) : null}

                                    {user.ability?.can(
                                        'manage',
                                        subject('Validation', {
                                            organizationUuid:
                                                project.organizationUuid,
                                            projectUuid: project.projectUuid,
                                        }),
                                    ) ? (
                                        <RouterNavLink
                                            label="Validator"
                                            exact
                                            to={`/generalSettings/projectManagement/${project.projectUuid}/validator`}
                                            leftSection={
                                                <MantineIcon
                                                    icon={IconChecklist}
                                                />
                                            }
                                        />
                                    ) : null}

                                    {user.ability?.can(
                                        'manage',
                                        subject('ContentVerification', {
                                            organizationUuid:
                                                project.organizationUuid,
                                            projectUuid: project.projectUuid,
                                        }),
                                    ) ? (
                                        <RouterNavLink
                                            label="Verified content"
                                            exact
                                            to={`/generalSettings/projectManagement/${project.projectUuid}/verifiedContent`}
                                            leftSection={
                                                <MantineIcon
                                                    icon={IconShieldCheck}
                                                />
                                            }
                                        />
                                    ) : null}

                                    {user.ability?.can(
                                        'promote',
                                        subject('SavedChart', {
                                            organizationUuid:
                                                project.organizationUuid,
                                            projectUuid: project.projectUuid,
                                        }),
                                    ) ? (
                                        <RouterNavLink
                                            label="Data ops"
                                            exact
                                            to={`/generalSettings/projectManagement/${project.projectUuid}/dataOps`}
                                            leftSection={
                                                <MantineIcon
                                                    icon={IconDatabaseExport}
                                                />
                                            }
                                        />
                                    ) : null}

                                    {health?.softDelete?.enabled && (
                                        <RouterNavLink
                                            label="Recently deleted"
                                            exact
                                            to={`/generalSettings/projectManagement/${project.projectUuid}/recentlyDeleted`}
                                            leftSection={
                                                <MantineIcon icon={IconTrash} />
                                            }
                                        />
                                    )}
                                </Box>
                            ) : null}
                        </Stack>
                    </ScrollArea>
                </Stack>
            }
        >
            {routeElements}
        </Page>
    );
};

export default Settings;
